import { useCallback, useMemo, useState } from 'react'
import { Row } from 'antd'
import cx from 'classnames'
import PropTypes from 'prop-types'
import { CSSTransition, TransitionGroup } from 'react-transition-group'

import { ImageType } from '../types'
import ImageItem from './ImageItem'
import { useTranslations } from 'common/language'
import { BlockSectionTitle, BlockWrapper, Container } from 'common/widgets'
import useQueryParam from 'hooks/useQueryParam'
import classNames from './styles.module.scss'


ImageGridWithFilter.propTypes = {
  data: PropTypes.shape({
    heading: PropTypes.string,
    heading_hx: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.shape({
      image: ImageType,
      tag: PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
      }),
      title: PropTypes.string,
      text: PropTypes.string,
      cta_link: PropTypes.string,
      cta_link_type: PropTypes.string,
      cta_link_caption: PropTypes.string,
    })),
    initialTag: PropTypes.string,
  }),
}

function TransitionRow(props) {
  return <Row {...props} gutter={[30, 30]} />
}

export default function ImageGridWithFilter({
  data = {
    columns: [],
  },
}) {
  const { columns = [], initialTag = null } = data
  const { gettext } = useTranslations()

  const tagToSlug = useMemo(() => {
    return [{ tag: { name: gettext('All'), slug: '' } }, ...columns].reduce((acc, { tag }) => {
      acc[tag.name] ??= tag.slug
      return acc
    }, {})
  }, [columns, gettext])
  const availableTags = useMemo(() => Object.keys(tagToSlug), [tagToSlug])

  const [selectedTag, setSelectedTag] = useState(availableTags.includes(initialTag) ? initialTag : gettext('All'))

  // Hook to update URL params
  const updateURLParam = useQueryParam('tag')

  const setTagHandler = useCallback(tag => {
    setSelectedTag('')
    setTimeout(() => {
      setSelectedTag(tag)
    }, 300)
    // Update URL immediately (only if URL params are enabled)
    if(initialTag !== null) {
      updateURLParam(tagToSlug[tag])
    }
  }, [updateURLParam, initialTag, tagToSlug])

  const tags = useMemo(() => {
    return availableTags.map((tag, idx) => (
      <li key={`${idx}-${tag}`} className={classNames.tag}>
        <button
          className={cx(classNames.tagBtn, tag === selectedTag && classNames.selected)}
          onClick={setTagHandler.bind(null, tag)}
        >
          {tag}
        </button>
      </li>
    ))
  }, [availableTags, selectedTag, setTagHandler])

  const items = useMemo(() => columns.map(({ tag, ...option }, idx) => {
    const tagName = tag.name
    return [gettext('All'), tagName].includes(selectedTag) && (
      <CSSTransition
        key={`${idx}-${tagName}`}
        timeout={300}
        classNames={{
          enter: classNames.enter,
          enterActive: classNames.enterActive,
          exit: classNames.exit,
          exitActive: classNames.exitActive,
        }}
      >
        <ImageItem {...option} caption={option.cta_link_caption} tag={tagName} />
      </CSSTransition>
    )
  }), [columns, selectedTag, gettext])

  return (
    <BlockWrapper>
      <Container size="xl">
        <BlockSectionTitle heading={data.heading} headingHx={data.heading_hx} />
        <ul className={classNames.tags}>
          {tags}
        </ul>
        <TransitionGroup component={TransitionRow}>
          {items}
        </TransitionGroup>
      </Container>
    </BlockWrapper>
  )
}
